import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowUpward } from '@mui/icons-material';
import styled from '@emotion/styled';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from 'Hooks/useDebounce';
import { uiTheme as t, Button, ConfirmDialog, Pagination, SegmentedTabs, SearchBox, FilterSelect } from 'Components/ui';
import employeesApi from 'Api/axios/employees-api';
import departmentsApi from 'Api/axios/departments-api';
import jobTitlesApi from 'Api/axios/job-titles-api';
import terminalsApi from 'Api/axios/terminals-api';
import useCourseMembers from './Hooks/useCourseMembers';
import MembersTable from './Components/MembersTable';
import AddMembersDrawer from './Components/AddMembersDrawer';

const CANDIDATES_PAGE_SIZE = 20;
const CANDIDATES_DEBOUNCE_MS = 300;

/** Los filtros viajan como lista separada por coma; vacío es "sin filtro". */
const asParam = ids => (ids.length > 0 ? ids.join(',') : undefined);

const PageBody = styled.div({
  padding: '28px 32px 56px',
});

const Header = styled.div({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: 20,
  flexWrap: 'wrap',
});

const Title = styled.h2({
  margin: 0,
  fontFamily: t.font,
  fontSize: 21,
  fontWeight: 600,
  color: t.color.title,
});

const Subtitle = styled.p({
  margin: '6px 0 0',
  maxWidth: 620,
  fontFamily: t.font,
  fontSize: 13.5,
  color: t.color.muted,
});

const Toolbar = styled.div({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  marginTop: 24,
  flexWrap: 'wrap',
});

const FilterRow = styled.div({
  display: 'flex',
  gap: 10,
  marginTop: 14,
  flexWrap: 'wrap',
});

// Dos componentes en vez de uno con prop `tone`: emotion reenvía al DOM las
// props que no reconoce, y React se queja de un atributo `tone` en un div.
const bannerBase = {
  marginTop: 18,
  padding: '12px 16px',
  borderRadius: t.radius.sm,
  fontFamily: t.font,
  fontSize: 13.5,
};

const InfoBanner = styled.div({
  ...bannerBase,
  background: t.color.countBg,
  color: t.color.countText,
});

const WarningBanner = styled.div({
  ...bannerBase,
  background: t.color.dangerSurface,
  color: t.color.danger,
});

// Flotante y no en el flujo: si empujara la tabla, marcar una casilla movería
// bajo el cursor la fila de al lado. z-index por debajo del Drawer (1200) y del
// Dialog (1300) de MUI, para que la barra no se monte sobre ellos.
const BulkBar = styled.div({
  position: 'fixed',
  bottom: 24,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1100,
  // Sin esto el bloque flotante encoge al ancho disponible y los botones se
  // parten en dos filas.
  width: 'max-content',
  maxWidth: 'calc(100vw - 48px)',
  padding: '10px 12px 10px 18px',
  display: 'flex',
  alignItems: 'center',
  gap: 18,
  background: t.color.surface,
  border: `1px solid ${t.color.primary}`,
  borderRadius: t.radius.button,
  boxShadow: '0 12px 32px rgba(16, 24, 40, 0.22)',
});

const BulkCount = styled.span({
  fontFamily: t.font,
  fontSize: 13.5,
  fontWeight: 600,
  color: t.color.title,
  whiteSpace: 'nowrap',
});

const BulkActions = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  whiteSpace: 'nowrap',
});

const LoadingState = styled.div({
  marginTop: 36,
  fontFamily: t.font,
  fontSize: 14,
  color: t.color.muted,
});

/**
 * Pestaña Miembros de la edición de un curso.
 *
 * Dos clases de miembro, y nada más: **obligatorios**, que tienen el curso
 * asignado o los dieron de alta a mano, y **opcionales**, a quienes se les
 * ofrece sin obligarlos. La clasificación la decide el backend, y ante la duda
 * gana la fila de inscripción.
 *
 * No hay columna de avance: eso vive en los reportes.
 */
export default function EditCourseMembers() {
  const { t2 } = useTranslation('EditCourse');
  const { courseId } = useParams();

  const {
    members,
    meta,
    pagination,
    page,
    goToPage,
    setPageSize,
    loading,
    tab,
    setTab,
    search,
    setSearch,
    filters,
    setFilter,
    readOnlyOptional,
    optionalWithoutAccess,
    addMembers,
    removeMembers,
    moveToMandatory,
  } = useCourseMembers(courseId);

  const [catalogs, setCatalogs] = useState({ departments: [], jobTitles: [], terminals: [] });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [removeTarget, setRemoveTarget] = useState(null);
  const [moveTarget, setMoveTarget] = useState(null);

  // Estado propio del panel de alta: su lista de candidatos es la plantilla
  // completa, no los miembros del curso.
  const [candidates, setCandidates] = useState([]);
  const [candidatesLoading, setCandidatesLoading] = useState(false);
  const [candidatesPage, setCandidatesPage] = useState(1);
  const [candidatesTotalPages, setCandidatesTotalPages] = useState(1);
  const [candidatesSearch, setCandidatesSearch] = useState('');
  const [candidatesFilters, setCandidatesFilters] = useState({
    departmentId: [],
    jobTitleId: [],
    terminalId: [],
  });

  // Sin esto la lista de candidatos pide una página por tecla pulsada.
  const debouncedCandidatesSearch = useDebounce(candidatesSearch, CANDIDATES_DEBOUNCE_MS);

  // Catálogos de los filtros. Se piden una vez: no cambian mientras la pantalla
  // está abierta.
  useEffect(() => {
    let mounted = true;

    const toOptions = rows =>
      (rows || []).map(row => ({ value: row.id, label: row.name })).filter(option => option.value && option.label);

    Promise.all([departmentsApi.readAll(), jobTitlesApi.readAll(), terminalsApi.readAll()]).then(
      ([departments, jobTitles, terminals]) => {
        if (!mounted) return;
        setCatalogs({
          departments: toOptions(departments.data),
          jobTitles: toOptions(jobTitles.data),
          terminals: toOptions(terminals.data),
        });
      }
    );

    return () => {
      mounted = false;
    };
  }, []);

  // Candidatos del panel. Solo se piden mientras está abierto.
  useEffect(() => {
    if (!drawerOpen) return undefined;

    let mounted = true;
    setCandidatesLoading(true);

    employeesApi
      .getUsers({
        search: debouncedCandidatesSearch.trim() || undefined,
        departmentId: asParam(candidatesFilters.departmentId),
        jobTitleId: asParam(candidatesFilters.jobTitleId),
        terminalId: asParam(candidatesFilters.terminalId),
        status: true,
        // Quien ya es miembro no vuelve a ofrecerse: añadirlo otra vez no
        // haría nada y la pantalla acabaría cantando un éxito vacío.
        excludeCourseId: courseId,
        page: candidatesPage,
        pageSize: CANDIDATES_PAGE_SIZE,
      })
      .then(res => {
        if (!mounted) return;
        setCandidates(
          (res.data || []).map(user => ({
            id: user.id,
            fullName: user.fullName,
            payrollNum: user.payrollNum ?? null,
            departmentName: user.Department ? user.Department.name : null,
            jobTitleName: user.JobTitle ? user.JobTitle.name : null,
            terminalName: user.Terminal ? user.Terminal.name : null,
          }))
        );
        setCandidatesTotalPages(res.pagination?.totalPages ?? 1);
        setCandidatesLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [drawerOpen, candidatesPage, debouncedCandidatesSearch, candidatesFilters, courseId]);

  /**
   * Antepone el número de afectados cuando la acción es masiva. Con una sola
   * persona el número no aporta nada y estorba.
   *
   * @param {string[]|null} ids - Objetivo del diálogo.
   * @param {string} text - Texto de la acción, escrito sin número.
   */
  const withCount = (ids, text) => (ids && ids.length > 1 ? `${ids.length} ${t2('SelectedCount')}. ${text}` : text);

  const tabs = [
    { value: 'mandatory', label: t2('TabMandatory'), count: meta.mandatory },
    { value: 'optional', label: t2('TabOptional'), count: meta.optional },
  ];

  // La pestaña de un curso abierto a toda la empresa ya no se bloquea entera.
  // Mover a obligatorios sí aplica ahí: cambia la inscripción de un empleado,
  // no la audiencia del curso. Y quitar de opcionales no hace falta apagarlo
  // desde aquí, porque los únicos que salen listados en ese alcance son los
  // que ya empezaron, y a esos la regla de `hasEnrollment` ya les apaga el
  // botón.

  const optionalEmptyLabel = readOnlyOptional ? t2('EmptyOptionalScopeAll') : t2('EmptyOptional');

  const tableLabels = useMemo(
    () => ({
      employee: t2('ColEmployee'),
      jobTitle: t2('ColJobTitle'),
      department: t2('ColDepartment'),
      terminal: t2('ColTerminal'),
      lastColumn: tab === 'mandatory' ? t2('ColOrigin') : t2('ColState'),
      originManual: t2('OriginManual'),
      originAssigned: t2('OriginAssigned'),
      stateNotStarted: t2('StateNotStarted'),
      stateInProgress: t2('StateInProgress'),
      stateApproved: t2('StateApproved'),
      stateFailed: t2('StateFailed'),
      remove: t2('RemoveFromCourse'),
      removeBlocked: t2('RemoveOptionalStartedBlocked'),
      moveToMandatory: t2('MoveToMandatory'),
      // En alcance 'all' el vacío no significa "no tiene opcionales" —los tiene
      // todos—, sino que nadie ha empezado. Decir lo primero sería mentira.
      empty: tab === 'mandatory' ? t2('EmptyMandatory') : optionalEmptyLabel,
    }),
    [tab, t2, optionalEmptyLabel]
  );

  const [selected, setSelected] = useState(new Set());

  // Misma regla que la tabla: a quien ya tiene inscripción no se le puede
  // quitar la invitación, porque no le quita nada. Basta con que uno de los
  // seleccionados lo sea para apagar el botón: la baja es en bloque y el
  // backend la rechaza entera con 409.
  const selectionHasStarted = useMemo(
    () => tab === 'optional' && members.some(member => member.hasEnrollment && selected.has(member.userId)),
    [tab, members, selected]
  );

  // Cualquier cosa que cambie la lista visible descarta la selección: pestaña,
  // página, búsqueda o filtros. Si no, una acción masiva podría alcanzar a
  // gente que el filtro acaba de esconder.
  useEffect(() => {
    setSelected(new Set());
  }, [tab, page, search, filters]);

  const toggle = userId => {
    setSelected(previous => {
      const next = new Set(previous);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const toggleAll = () => {
    setSelected(previous => {
      const ids = members.map(member => member.userId);
      const all = ids.length > 0 && ids.every(id => previous.has(id));
      return all ? new Set() : new Set(ids);
    });
  };

  const confirmRemove = async () => {
    const ids = removeTarget;
    setRemoveTarget(null);

    const ok = await removeMembers(ids);

    if (ok) {
      setSelected(new Set());
      toast.success(t2('ToastMembersRemoved'));
    } else {
      toast.error(t2('ToastMembersError'));
    }
  };

  // Mover no destruye nada, pero cambia el régimen del empleado y lo saca de
  // los opcionales, así que tampoco puede dispararse de un solo clic.
  const confirmMove = async () => {
    const ids = moveTarget;
    setMoveTarget(null);

    const ok = await moveToMandatory(ids);

    if (ok) {
      setSelected(new Set());
      toast.success(t2('ToastMembersMoved'));
    } else toast.error(t2('ToastMembersError'));
  };

  /**
   * El alta es idempotente, así que "sin error" no significa "se añadió a
   * alguien". Con la lista de candidatos ya filtrada esto solo ocurre si otra
   * persona los metió mientras el panel estaba abierto, pero decirlo es más
   * barato que dejar al administrador creyendo que hizo algo.
   */
  const handleAdd = async (userIds, membership) => {
    const { ok, added } = await addMembers(userIds, membership);

    if (!ok) {
      toast.error(t2('ToastMembersError'));
      return false;
    }

    if (added === 0) {
      toast.error(t2('ToastMembersAlreadyIn'));
      return false;
    }

    toast.success(t2('ToastMembersAdded'));
    return true;
  };

  return (
    <PageBody>
      <Header>
        <div>
          <Title>{t2('MembersTitle')}</Title>
          <Subtitle>{t2('MembersSubtitle')}</Subtitle>
        </div>
        <Button type="button" variant="primary" onClick={() => setDrawerOpen(true)}>
          {t2('AddMembers')}
        </Button>
      </Header>

      <Toolbar>
        <SearchBox value={search} placeholder={t2('MembersSearchPlaceholder')} onChange={setSearch} />
        <SegmentedTabs options={tabs} value={tab} onChange={setTab} />
      </Toolbar>

      <FilterRow>
        <FilterSelect
          value={filters.terminalId}
          options={catalogs.terminals}
          placeholder={t2('ColTerminal')}
          onChange={value => setFilter('terminalId', value)}
        />
        <FilterSelect
          value={filters.jobTitleId}
          options={catalogs.jobTitles}
          placeholder={t2('ColJobTitle')}
          onChange={value => setFilter('jobTitleId', value)}
        />
        <FilterSelect
          value={filters.departmentId}
          options={catalogs.departments}
          placeholder={t2('ColDepartment')}
          onChange={value => setFilter('departmentId', value)}
        />
      </FilterRow>

      {tab === 'optional' && readOnlyOptional ? <InfoBanner>{t2('BannerScopeAll')}</InfoBanner> : null}
      {tab === 'optional' && optionalWithoutAccess ? <WarningBanner>{t2('BannerNotOptional')}</WarningBanner> : null}

      {/* La barra solo existe mientras haya selección: fuera de ese momento
          ocuparía sitio para no ofrecer nada. */}
      {selected.size > 0 ? (
        <BulkBar>
          <BulkCount>
            {selected.size} {t2('SelectedCount')}
          </BulkCount>
          <BulkActions>
            <Button type="button" variant="secondary" size="sm" onClick={() => setSelected(new Set())}>
              {t2('BulkClear')}
            </Button>
            {tab === 'optional' ? (
              <Button type="button" variant="primary" size="sm" onClick={() => setMoveTarget([...selected])}>
                {t2('MoveToMandatory')}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="danger"
              size="sm"
              disabled={selectionHasStarted}
              title={selectionHasStarted ? t2('RemoveOptionalStartedBlocked') : undefined}
              onClick={() => setRemoveTarget([...selected])}
            >
              {t2('RemoveFromCourse')}
            </Button>
          </BulkActions>
        </BulkBar>
      ) : null}

      {loading && members.length === 0 ? (
        <LoadingState>{t2('MembersLoading')}</LoadingState>
      ) : (
        <>
          <MembersTable
            rows={members}
            tab={tab}
            labels={tableLabels}
            canManage
            selected={selected}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onRemove={userId => setRemoveTarget([userId])}
            onMove={userId => setMoveTarget([userId])}
          />
          {pagination && pagination.totalItems > 0 ? (
            <Pagination
              page={page}
              pageSize={pagination.pageSize}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              onChange={goToPage}
              onPageSizeChange={setPageSize}
              labels={{
                showing: t2('PaginationShowing'),
                of: t2('PaginationOf'),
                page: t2('PaginationPage'),
                prev: t2('PaginationPrev'),
                next: t2('PaginationNext'),
                first: t2('PaginationFirst'),
                last: t2('PaginationLast'),
                perPage: t2('PaginationPerPage'),
              }}
            />
          ) : null}
        </>
      )}

      <AddMembersDrawer
        open={drawerOpen}
        labels={{
          title: t2('AddMembers'),
          subtitle: t2('AddMembersSubtitle'),
          close: t2('ConfirmCancel'),
          asMandatory: t2('AddAsMandatory'),
          asOptional: t2('AddAsOptional'),
          searchPlaceholder: t2('MembersSearchPlaceholder'),
          department: t2('ColDepartment'),
          jobTitle: t2('ColJobTitle'),
          terminal: t2('ColTerminal'),
          selectPage: t2('SelectPage'),
          selectedCount: t2('SelectedCount'),
          empty: t2('EmptyCandidates'),
          cancel: t2('ConfirmCancel'),
          submit: t2('AddMembers'),
          loading: t2('MembersLoading'),
        }}
        candidates={candidates}
        loading={candidatesLoading}
        page={candidatesPage}
        totalPages={candidatesTotalPages}
        onPageChange={setCandidatesPage}
        search={candidatesSearch}
        onSearchChange={value => {
          setCandidatesSearch(value);
          setCandidatesPage(1);
        }}
        departments={catalogs.departments}
        jobTitles={catalogs.jobTitles}
        terminals={catalogs.terminals}
        filters={candidatesFilters}
        onFilterChange={(key, value) => {
          setCandidatesFilters(previous => ({ ...previous, [key]: value }));
          setCandidatesPage(1);
        }}
        allowOptional={!readOnlyOptional}
        onClose={() => setDrawerOpen(false)}
        onSubmit={handleAdd}
      />

      <ConfirmDialog
        open={removeTarget !== null}
        title={tab === 'mandatory' ? t2('ConfirmRemoveMandatoryTitle') : t2('ConfirmRemoveOptionalTitle')}
        description={withCount(
          removeTarget,
          tab === 'mandatory' ? t2('ConfirmRemoveMandatoryText') : t2('ConfirmRemoveOptionalText')
        )}
        cancelLabel={t2('ConfirmCancel')}
        confirmLabel={t2('ConfirmAccept')}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={confirmRemove}
      />

      <ConfirmDialog
        open={moveTarget !== null}
        tone="primary"
        icon={<ArrowUpward />}
        title={t2('ConfirmMoveTitle')}
        description={withCount(moveTarget, t2('ConfirmMoveText'))}
        cancelLabel={t2('ConfirmCancel')}
        confirmLabel={t2('ConfirmAccept')}
        onCancel={() => setMoveTarget(null)}
        onConfirm={confirmMove}
      />
    </PageBody>
  );
}
